import React, { useState } from "react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles } from "lucide-react";

/**
 * AskTheCollectiveComposer — the new Ask the Collective entry point with
 * N8 Guide pre-publish review built in.
 *
 * Review states returned by `/collective/questions/review`:
 *   action="allow"   → publish straight away
 *   action="reframe" → show the N8 Guide suggestion; author can USE SUGGESTION
 *                      or EDIT MY QUESTION (never force rewrite)
 *   action="block"   → hard boundary; author must edit before posting
 *                      (no "Post Anyway" affordance by design)
 *
 * The actual post creation reuses the existing `endpoints.createPost`
 * endpoint with `is_question: true` so the Collective's question
 * pathway and all its downstream wiring stay unchanged.
 */
export default function AskTheCollectiveComposer({
  open, onOpenChange, onPosted, createPost,
}) {
  const [text, setText] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState(null);  // {action, suggestion?, reason?, notice?}
  const [posting, setPosting] = useState(false);

  const close = () => {
    onOpenChange(false);
    setText("");
    setReview(null);
  };

  const runReview = async () => {
    const content = text.trim();
    if (!content) return;
    setReviewing(true);
    try {
      const { data } = await endpoints.collectiveReviewQuestion(content);
      setReview(data);
      if (data.action === "allow") {
        // No intervention — publish immediately.
        await publish(content, data?.notice);
      }
    } catch {
      toast.error("Couldn't review that right now.");
    } finally { setReviewing(false); }
  };

  const publish = async (content, notice) => {
    setPosting(true);
    try {
      await createPost({ content, is_question: true });
      if (notice) toast(notice, { duration: 6000 });
      else toast.success("Posted to the Collective.");
      onPosted?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post that.");
    } finally { setPosting(false); }
  };

  const useSuggestion = async () => {
    const next = (review?.suggestion || "").trim();
    if (!next) return;
    await publish(next, null);
  };

  const editMine = () => {
    // Keep the author's text, drop the dialog so they can edit freely.
    setReview(null);
  };

  const isBlocked = review?.action === "block";
  const isReframe = review?.action === "reframe";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="bg-[hsl(var(--paper))] max-w-lg" data-testid="ask-collective-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Ask the Collective</DialogTitle>
          <DialogDescription className="text-[hsl(var(--muted-foreground))]">
            Ask for perspective from people walking their own becoming journey.
            Ask for perspective. Share experience. No hype. No selling.
          </DialogDescription>
        </DialogHeader>

        {!review && (
          <>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              maxLength={1000}
              data-testid="ask-collective-textarea"
              placeholder="What are you hoping to learn from the Collective today?"
              className="min-h-[140px]"
            />
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] text-right">{text.length}/1000</div>
            <DialogFooter>
              <Button variant="outline" onClick={close} data-testid="ask-collective-cancel" className="rounded-full">
                Cancel
              </Button>
              <Button
                onClick={runReview}
                disabled={reviewing || posting || !text.trim()}
                data-testid="ask-collective-submit"
                className="rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90"
              >
                {reviewing || posting ? "Checking…" : "Ask the Collective"}
              </Button>
            </DialogFooter>
          </>
        )}

        {isReframe && (
          <div className="space-y-4" data-testid="ask-collective-reframe">
            <div className="rounded-md border hairline bg-[hsl(var(--sand))]/40 p-4">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--terracotta))]">
                <Sparkles size={11} /> N8 Guide suggestion
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-[hsl(var(--fg))]/90">
                {review.reason}
              </p>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                Your question
              </div>
              <p
                className="mt-1 font-display italic text-[15px] leading-snug text-[hsl(var(--fg))]/80"
                data-testid="ask-collective-original"
              >
                “{text}”
              </p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
                Suggested
              </div>
              <p
                className="mt-1 font-display italic text-[16px] leading-snug text-[hsl(var(--fg))]"
                data-testid="ask-collective-suggestion"
              >
                “{review.suggestion}”
              </p>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={editMine}
                data-testid="ask-collective-edit-mine"
                className="rounded-full"
              >
                Edit my question
              </Button>
              <Button
                onClick={useSuggestion}
                disabled={posting}
                data-testid="ask-collective-use-suggestion"
                className="rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90"
              >
                {posting ? "Posting…" : "Use suggestion"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {isBlocked && (
          <div className="space-y-4" data-testid="ask-collective-block">
            <div className="rounded-md border hairline bg-[hsl(var(--terracotta))]/10 p-4">
              <div className="text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--terracotta))]">
                This can&apos;t be posted as written
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-[hsl(var(--fg))]/90">
                {review.reason}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} className="rounded-full">Cancel</Button>
              <Button
                onClick={editMine}
                data-testid="ask-collective-block-edit"
                className="rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90"
              >
                Edit my question
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
